import React, { useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import { cn } from '@/shared/ui/utils/tailwind';
import { useTranslation } from 'react-i18next';
import type { Calendar } from '@services/requests/housien-models/Calendar';
import type { ExpandedEvent } from '@features/agenda/utils/expand-recurrence';
import { isSameDay, weekDates } from '@features/agenda/utils/agenda-dates';
import { eventColor } from '../event-color';
import { DAY_START_HOUR, MIN_CHIP_HEIGHT, eventPlacement, nowOffset } from '../time-grid';
import { Text, UnstyledButton } from '@/shared/ui';

interface MobileWeekViewProps {
  anchor: Date;
  events: ExpandedEvent[];
  calendars: Calendar[];
  onSelect: (event: ExpandedEvent) => void;
  onDayClick: (date: Date) => void;
  onSlotClick?: (date: Date) => void;
}

/** Pixel height of one hour row — compact enough for a 7-column mobile grid. */
const SLOT_HEIGHT = 48;
/** Width of the hour-label gutter, in px. Kept in sync with the header spacer. */
const GUTTER_WIDTH = 40;
/** Hour scrolled into view when the displayed week doesn't contain today. */
const FALLBACK_SCROLL_HOUR = 7;

const HOURS = Array.from({ length: 24 }, (_, i) => i + DAY_START_HOUR);
const GRID_COLUMNS = `${GUTTER_WIDTH}px repeat(7, minmax(0, 1fr))`;

/**
 * Compact weekly overview showing event blocks in a 7-column grid with an
 * hour-label gutter, so the timeline stays readable even on an empty week.
 *
 * @param props Anchor date, events, calendars, and handlers.
 * @returns Rendered mobile week view.
 */
export const MobileWeekView: React.FC<MobileWeekViewProps> = ({
  anchor,
  events,
  calendars,
  onSelect,
  onDayClick,
  onSlotClick,
}) => {
  const { t } = useTranslation('features/agenda');
  const days = weekDates(anchor);
  const scrollRef = useRef<HTMLDivElement>(null);
  const todayIndex = days.findIndex((d) => dayjs().isSame(dayjs(d), 'day'));
  const now = todayIndex >= 0 ? nowOffset(days[todayIndex], SLOT_HEIGHT, 24) : null;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const target = now ?? FALLBACK_SCROLL_HOUR * SLOT_HEIGHT;
    el.scrollTop = Math.max(0, target - el.clientHeight / 3);
  }, [now]);

  return (
    <div data-testid="agenda-mobile-week" className="flex h-full flex-col overflow-hidden">
      {/* Day header strip — leading spacer keeps columns aligned with the gutter */}
      <div
        className="grid border-b border-[var(--border-subtle)] bg-[var(--surface-primary)]"
        style={{ gridTemplateColumns: GRID_COLUMNS }}
      >
        <div aria-hidden="true" />
        {days.map((d, i) => {
          const isToday = i === todayIndex;
          return (
            <UnstyledButton
              key={i}
              type="button"
              onClick={() => onDayClick(d)}
              className="flex flex-col items-center py-(--space-2)"
              style={{ gap: 2 }}
            >
              <Text className="text-[length:var(--text-micro)] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
                {t(`weekday_short.${dayjs(d).day()}`)}
              </Text>
              <div
                className={cn('flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold')}
                style={{
                  background: isToday ? 'var(--seed-base)' : 'transparent',
                  color: isToday ? '#fff' : 'var(--text-primary)',
                }}
              >
                {dayjs(d).format('D')}
              </div>
            </UnstyledButton>
          );
        })}
      </div>

      {/* Time grid */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto"
        style={{ paddingBottom: 'calc(var(--space-16) + env(safe-area-inset-bottom, 0px))' }}
      >
        <div className="relative" style={{ height: HOURS.length * SLOT_HEIGHT }}>
          {/* Hour rows: label in the gutter, a divider line across the 7 days */}
          {HOURS.map((h) => (
            <div
              key={h}
              className="absolute inset-x-0 grid"
              style={{
                top: (h - DAY_START_HOUR) * SLOT_HEIGHT,
                height: SLOT_HEIGHT,
                gridTemplateColumns: GRID_COLUMNS,
              }}
            >
              <Text className="pr-(--space-1) pt-px text-right text-[length:var(--text-micro)] font-medium tabular-nums text-[var(--text-tertiary)]">
                {String(h).padStart(2, '0')}:00
              </Text>
              {days.map((d, colIdx) => (
                <UnstyledButton
                  key={colIdx}
                  type="button"
                  aria-label={`${dayjs(d).format('DD/MM')} ${String(h).padStart(2, '0')}:00`}
                  disabled={!onSlotClick}
                  onClick={() => {
                    if (!onSlotClick) return;
                    const slot = new Date(d);
                    slot.setHours(h, 0, 0, 0);
                    onSlotClick(slot);
                  }}
                  className="border-t border-r border-[var(--border-ultra-subtle)]"
                  style={{ height: SLOT_HEIGHT }}
                >
                  {null}
                </UnstyledButton>
              ))}
            </div>
          ))}

          {/* Current-time line, spanning only today's column */}
          {now !== null && (
            <div
              data-testid="agenda-now-line"
              aria-hidden="true"
              className="pointer-events-none absolute z-10 grid"
              style={{ top: now, insetInline: 0, gridTemplateColumns: GRID_COLUMNS }}
            >
              <div />
              {days.map((_, colIdx) => (
                <div key={colIdx} className="flex items-center">
                  {colIdx === todayIndex && (
                    <Text component="span" className="h-px flex-1 bg-[var(--status-error)]" />
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Event chips, positioned over their day column */}
          <div
            className="pointer-events-none absolute inset-0 grid"
            style={{ gridTemplateColumns: GRID_COLUMNS }}
          >
            <div />
            {days.map((d, colIdx) => {
              const dayEvs = events.filter((e) => isSameDay(e.start_at_utc, d));
              return (
                <div key={colIdx} className="relative">
                  {dayEvs.map((ev) => {
                    const color = eventColor(ev, calendars);
                    const { top, height } = eventPlacement(ev, SLOT_HEIGHT, MIN_CHIP_HEIGHT);
                    return (
                      <UnstyledButton
                        key={`${ev.event_id}-${ev.occurrence_start}`}
                        type="button"
                        data-testid="agenda-mobile-week-chip"
                        onClick={() => onSelect(ev)}
                        className="pointer-events-auto absolute overflow-hidden active:opacity-80"
                        style={{
                          top,
                          height,
                          left: 2,
                          right: 2,
                          borderRadius: 'var(--radius-sm)',
                          borderLeft: `2px solid ${color}`,
                          background: `color-mix(in srgb, ${color} 18%, var(--surface-primary))`,
                        }}
                      >
                        <Text
                          className="truncate px-px text-[length:var(--text-micro)] font-bold"
                          style={{ color }}
                        >
                          {dayjs(ev.start_at_utc).format('HH:mm')}
                        </Text>
                      </UnstyledButton>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
